/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import AnalyticsProviderServiceBase from "bundle/service/AnalyticsProviderService";
import {
    auth2 as auth, logging, analytics, providers,
} from "dc-core";
import { dcAnalytics } from "dc-analytics-provider";
import ServiceRegistry from "bundle/util/ServiceRegistry";

const CUSTOM_EVENTS = {
    workflows: {
        brandArea: ["BrandArea", "Use", "Clicked"],
        hudBrandArea: ["HUDBrandArea", "Use", "Clicked"],
        helpIcon: ["Help", "Use", "Clicked"],
        feedbackIcon: ["Feedback", "Use", "Clicked"],
        aboutIcon: ["About", "Use", "Clicked"],
        legalNotices: ["Legal Notices", "Use", "Clicked"],
        thumbnailView: ["Thumbnail", "View", "Open"],
        legalNoticesClose: ["Legal Notices", "Close", "Clicked"],
        legalNoticesPrivacy: ["Legal Notices", "Privacy", "Clicked"],
        legalNoticesCookies: ["Legal Notices", "Cookies", "Clicked"],
        legalNoticesCMP: ["Legal Notices", "CMPTool", "Clicked"],
        legalNoticesTerms: ["Legal Notices", "Terms", "Clicked"],
        legalNoticesPoweredBy: ["Legal Notices", "Powered By", "Clicked"],
        legalNoticesDoNotSell: ["Legal Notices", "Do Not Sell", "Clicked"],
        legalNoticesTermsThirdParty: ["Legal Notices", "Third Party", "Clicked"],
        sideMenuCallToAction: ["SideMenuCallToAction", "Use", "Clicked"],
        sideToolCallToAction: ["SideToolCallToAction", "Use", "Clicked"],
        filePreview: ["preview", "show", "show"],
        signInIcon: ["SignIn", "Use", "Clicked"],
        signOutIcon: ["SignOut", "Use", "Clicked"],
        breadcrumbClick: ["Breadcrumb", "Use", "Clicked"],

        createPdfPickerOpen: ["CreatePdfPickerOpen", "Use", "Clicked"],
        createPdfPickerOpenSuccess: ["CreatePdfPickerOpenSuccess", "Use", "Clicked"],
        createPdfPickerOpenNewSuccess: ["CreatePdfPickerOpenNewSuccess", "Use", "Clicked"],
        createPdfPickerOpenCancel: ["CreatePdfPickerOpenCancel", "Use", "Clicked"],

        importNotRegisterd: ["Import", "Not", "Registered"],
        importStart: ["Import", "execute", "Start"],
        importSuccess: ["Import", "execute", "Success"],
        importFail: ["Import", "execute", "Fail"],

        exportNotRegisterd: ["Export", "Not", "Registered"],
        exportStart: ["Export", "execute", "Start"],
        exportSuccess: ["Export", "execute", "Success"],
        exportFail: ["Export", "execute", "Fail"],
        upsellBlockedView: ["UpsellBlockedView", "Use", "Success"],
        upsellPlanView: ["UpsellPlanView", "Use", "Success"],
        upsellViewPlansClicked: ["UpsellViewPlansClicked", "Use", "Success"],
        upsellSignInClicked: ["UpsellSignInClicked", "Use", "Success"],
        helpUrl: ["helpUrl", "Use", "Success"],
        upsellConfirmClicked: ["UpsellConfirmClicked", "Use", "Success"],
        upsellConfirmSuccess: ["UpsellConfirmSuccess", "Use", "Success"],
        upsellConfirmFailure: ["UpsellConfirmFailure", "Use", "Fail"],
        screenBlocked: ["SreenBlocked", "Use", "Success"],
        toastShown: ["ToastShown", "Use", "Success"],

        tutorialWelcomeDialogShow: ["Tutorial", "WelcomeDialog", "Show"],
        tutorialWelcomeDialogTakeATourClicked: ["Tutorial", "WelcomeDialog", "TakeATour", "Clicked"],
        tutorialWelcomeDialogSkipClicked: ["Tutorial", "WelcomeDialog", "Skip", "Clicked"],
        tutorialWelcomeDialogCloseClicked: ["Tutorial", "WelcomeDialog", "Close", "Clicked"],
        tutorialWelcomeDialogEscapeClicked: ["Tutorial", "WelcomeDialog", "Escape", "Clicked"],
        tutorialFreeToolsShow: ["Tutorial", "FreeTools", "Show"],
        tutorialFreeToolsSkipClicked: ["Tutorial", "FreeTools", "Skip", "Clicked"],
        tutorialFreeToolsNextClicked: ["Tutorial", "FreeTools", "Next", "Clicked"],
        tutorialFreeToolsHide: ["Tutorial", "FreeTools", "Hidden"],
        tutorialFreeToolsDismissed: ["Tutorial", "FreeTools", "Dismissed"],
        tutorialMoreToolsShow: ["Tutorial", "MoreTools", "Show"],
        tutorialMoreToolsSkipClicked: ["Tutorial", "MoreTools", "Skip", "Clicked"],
        tutorialMoreToolsNextClicked: ["Tutorial", "MoreTools", "Next", "Clicked"],
        tutorialMoreToolsDoneClicked: ["Tutorial", "MoreTools", "Done", "Clicked"],
        tutorialMoreToolsHide: ["Tutorial", "MoreTools", "Hidden"],
        tutorialMoreToolsDismissed: ["Tutorial", "MoreTools", "Dismissed"],
        notifyUserDialogShow: ["Tutorial", "NotifyUserDialog", "Show"],
        notifyUserDialogCopyContent: ["Tutorial", "NotifyUserDialog", "CopyContent", "Click"],
        notifyUserDialogSendEmail: ["Tutorial", "NotifyUserDialog", "SendEmail", "Click"],
        notifyUserDialogClose: ["Tutorial", "NotifyUserDialog", "Close", "Click"],
        notifyUserDialogDone: ["Tutorial", "NotifyUserDialog", "Done", "Click"],
        notifyUserDialogEscape: ["Tutorial", "NotifyUserDialog", "Escape", "Click"],
        editHeaderDownloadButtonClicked: ["Edit Web", "EditTopBar", "DownloadButton", "Click"],
        editHeaderCancelButtonClicked: ["Edit Web", "EditTopBar", "CancelButton", "Click"],
        openInDesktopAppButtonClicked: ["OpenInDesktop", "OpenInDesktopAppButton", "Click"],
        openInDesktopButtonClicked: ["OpenInDesktop", "OpenInDesktopButton", "Click"],
        openInDesktopCancelButtonClicked: ["OpenInDesktop", "CancelButton", "Click"],
        openInDesktopInstallHereLinkClicked: ["OpenInDesktop", "InstallHere", "Click"],
    },
    customs: {
        clickCtxt: "adb.event.context.dcweb.clickcontext",
    },
};

export default class AnalyticsProviderService extends AnalyticsProviderServiceBase {
    get CUSTOM_EVENTS() {
        return CUSTOM_EVENTS;
    }

    initialize() {
        ServiceRegistry.getService("CMPToolService").ready().then(() => {
            setTimeout(this._initAnalytics.bind(this), 2000);
        });
    }

    logEvent(event, custom = {}) {
        if (!window.adobe_dc_sdk.gdpr_consent) return;

        super.logEvent(event, {
            ...custom,
            enableLogging: window.adobe_dc_sdk.gdpr_consent,
            enableAnalytics: window.adobe_dc_sdk.gdpr_consent,
        });
    }

    _getAnalyticsConfig() {
        return {
            allowedKeys: {
                event: ["workflow", "subcategory", "type", "subtype", "value"],
                content: ["*"],
                source: ["name", "version"],
                custom: ["*"],
            },
            enableAnalytics: window.adobe_dc_sdk.gdpr_consent,
            enableLogging: window.adobe_dc_sdk.gdpr_consent,
        };
    }

    _getCustomFields() {
        let tenantId;
        let externalUserId;
        let callingApp = "dc-view-sdk";
        const config = ServiceRegistry.getService("AppStore").getConfig();
        if (config) {
            const analyticsInfo = config.analyticsInfo;
            if (analyticsInfo && analyticsInfo.callingApp) {
                callingApp = analyticsInfo.callingApp;
            }
            if (analyticsInfo && analyticsInfo.tenantId) {
                tenantId = analyticsInfo.tenantId;
            } else {
                tenantId = config.eventOrigin || config.clientId;
            }
            if (analyticsInfo && analyticsInfo.externalUserId) {
                externalUserId = analyticsInfo.externalUserId;
            } else {
                externalUserId = config.windowHref || "unknown";
            }
        }

        return {
            tenant_id: tenantId,
            external_user_id: externalUserId,
            "adb.event.context.dc.calling.app": callingApp,
        };
    }

    _initAnalytics() {
        if (!window.adobe_dc_sdk.gdpr_consent) return;

        // TODO:: discuss if we should override addDefaults2 as well.
        const _addDefaults = dcAnalytics._addDefaults.bind(dcAnalytics);
        dcAnalytics._addDefaults = (...args) => {
            const fields = _addDefaults(...args);
            if (fields) {
                fields.custom = { ...fields.custom || {}, ...this._getCustomFields() };
                return fields;
            }

            return {
                custom: this._getCustomFields(),
            };
        };

        dcAnalytics.initialize(this._getAnalyticsConfig(), logging, auth, analytics);

        providers.user().then(user => {
            dcAnalytics.setUser(user);
        }).catch(error => ServiceRegistry.getService("LoggingService").error(
            "AnalyticsProviderService",
            "Error in setting user for analytics ",
            error,
        ));
    }

    logInitialPresetEvent(confDiff) {
        const preset = confDiff.embedMode;
        const workflow = "FilePreview";
        this.logEvent({
            workflow, subcategory: "embedMode", type: preset,
        });
        Object.keys(confDiff)
            .filter(e => e !== "embedMode" && e !== "localizationStrings")
            .forEach(type => {
                this.logEvent({
                    workflow, subcategory: preset, type,
                });
            });
    }
}
